// ts-rest contract for the Administration "last run" panel (SEP-195,
// ADR-0009).
//
// The Client Book upload doors returned their report in the HTTP response and
// stored none of it, so the reason a row did not land was gone when the upload
// drawer closed. `IngestRun` and `IngestRunException` now keep the run, and
// this contract is how the Admin shell reads it back.
//
// Endpoints under /api/ingest-runs:
//   GET /api/ingest-runs/latest          — the newest run of each door
//   GET /api/ingest-runs/:id/exceptions  — every reported row of one run

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

export const ingestDoorSchema = z.enum([
    'contacts',
    'client_book_mapping',
    'kyc_assessments',
    'crm_fact_finds',
    'valuation_grid',
    'dynamics_contacts',
    'benchmark_pci',
]);
export type IngestDoor = z.infer<typeof ingestDoorSchema>;

/** How many rows carried one reason, for the panel's histogram. */
export const ingestReasonCountSchema = z.object({
    reason: z.string(),
    count: z.number().int().nonnegative(),
});
export type IngestReasonCount = z.infer<typeof ingestReasonCountSchema>;

export const ingestRunSummarySchema = z.object({
    id: z.string(),
    door: ingestDoorSchema,
    filename: z.string(),
    ranAt: z.string(),
    /** Resolved display name of the person who ran it, or null. */
    ranByName: z.string().nullable(),
    parsed: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /** Rows that landed with no link and no owner. They ARE imported. */
    unlinked: z.number().int().nonnegative(),
    conflicts: z.number().int().nonnegative(),
    /** The reason histogram, largest first. */
    reasons: z.array(ingestReasonCountSchema),
});
export type IngestRunSummary = z.infer<typeof ingestRunSummarySchema>;

export const latestIngestRunsResponseSchema = z.object({
    runs: z.array(ingestRunSummarySchema),
});
export type LatestIngestRunsResponse = z.infer<typeof latestIngestRunsResponseSchema>;

export const ingestRunExceptionSchema = z.object({
    reason: z.string(),
    detail: z.string(),
    ref: z.string().nullable(),
});
export type IngestRunExceptionRow = z.infer<typeof ingestRunExceptionSchema>;

export const ingestRunExceptionsResponseSchema = z.object({
    runId: z.string(),
    exceptions: z.array(ingestRunExceptionSchema),
});
export type IngestRunExceptionsResponse = z.infer<typeof ingestRunExceptionsResponseSchema>;

export const ingestRunsContract = c.router(
    {
        latest: {
            method: 'GET',
            path: '/ingest-runs/latest',
            responses: { 200: latestIngestRunsResponseSchema },
            summary: 'The newest run of each Client Book upload door, with its reason histogram',
        },
        exceptions: {
            method: 'GET',
            path: '/ingest-runs/:id/exceptions',
            pathParams: z.object({ id: z.string() }),
            responses: { 200: ingestRunExceptionsResponseSchema, 404: errorBody },
            summary: 'Every reported row of one ingest run, for review and CSV export',
        },
    },
    { pathPrefix: '/api' },
);
